import React, { useState, useEffect } from 'react';
import { FaTools, FaSearch, FaPlus, FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import axios from 'axios';

const Eftersyn = () => {
  const [eftersyn, setEftersyn] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEftersyn, setEditingEftersyn] = useState(null);
  const [formData, setFormData] = useState({
    bil_id: '',
    eftersyn_dato: '',
    eftersyn_type: '',
    beskrivelse: '',
    status: 'Planlagt',
    noter: '',
    godkendt_status: 'Godkendt',
    ansvarlig: '',
    godkendt_af: '',
    sidste_eftersyn: '',
    naeste_eftersyn: ''
  });

  useEffect(() => {
    fetchEftersyn();
  }, []);

  const fetchEftersyn = async () => {
    try {
      const response = await axios.get('/api/vaerksted/eftersyn');
      setEftersyn(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching eftersyn:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEftersyn) {
        await axios.put(`/api/vaerksted/eftersyn/${editingEftersyn.id}`, formData);
      } else {
        await axios.post('/api/vaerksted/eftersyn', formData);
      }
      setShowModal(false);
      setEditingEftersyn(null);
    setFormData({
        bil_id: '',
        eftersyn_dato: '',
        eftersyn_type: '',
        beskrivelse: '',
        status: 'Planlagt',
      noter: '',
      godkendt_status: 'Godkendt',
      ansvarlig: '',
      godkendt_af: '',
      sidste_eftersyn: '',
      naeste_eftersyn: ''
      });
      fetchEftersyn();
    } catch (error) {
      console.error('Error saving eftersyn:', error);
    }
  };

  const handleEdit = (eft) => {
    setEditingEftersyn(eft);
    setFormData({
      bil_id: eft.bil_id || '',
      eftersyn_dato: eft.eftersyn_dato || '',
      eftersyn_type: eft.eftersyn_type || '',
      beskrivelse: eft.beskrivelse || '',
      status: eft.status || 'Planlagt',
      noter: eft.noter || '',
      godkendt_status: eft.godkendt_status || 'Godkendt',
      ansvarlig: eft.ansvarlig || '',
      godkendt_af: eft.godkendt_af || '',
      sidste_eftersyn: eft.sidste_eftersyn || '',
      naeste_eftersyn: eft.naeste_eftersyn || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på, at du vil slette dette eftersyn?')) {
      try {
        await axios.delete(`/api/vaerksted/eftersyn/${id}`);
        fetchEftersyn();
      } catch (error) {
        console.error('Error deleting eftersyn:', error);
      }
    }
  };

  const filteredEftersyn = eftersyn.filter(eft =>
    eft.bil_internt_nummer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    eft.eftersyn_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    eft.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Eftersyn</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-falck-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
        >
          <FaPlus /> Tilføj eftersyn
        </button>
      </div>

      {/* Søgefelt */}
      <div className="mb-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Søg efter eftersyn..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-falck-blue focus:border-transparent"
          />
        </div>
      </div>

      {/* Eftersyn tabel */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow overflow-hidden">
        <div className="scroll-fade-x"><div className="scroll-area">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Bil
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Eftersyn dato
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Beskrivelse
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Handlinger
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredEftersyn.map((eft) => (
              <tr key={eft.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {eft.bil_internt_nummer}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(eft.eftersyn_dato).toLocaleDateString('da-DK')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {eft.eftersyn_type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    eft.status === 'Planlagt' ? 'bg-yellow-100 text-yellow-800' :
                    eft.status === 'I gang' ? 'bg-blue-100 text-blue-800' :
                    eft.status === 'Afsluttet' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {eft.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {eft.beskrivelse || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleEdit(eft)}
                      className="text-falck-blue hover:text-blue-700"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(eft.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div></div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingEftersyn ? 'Rediger eftersyn' : 'Tilføj nyt eftersyn'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bil ID
                </label>
                <input
                  type="text"
                  value={formData.bil_id}
                  onChange={(e) => setFormData({...formData, bil_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Eftersyn dato
                </label>
                <input
                  type="date"
                  value={formData.eftersyn_dato}
                  onChange={(e) => setFormData({...formData, eftersyn_dato: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Eftersyn type
                </label>
                <select
                  value={formData.eftersyn_type}
                  onChange={(e) => setFormData({...formData, eftersyn_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  required
                >
                  <option value="">Vælg type</option>
                  <option value="El eftersyn personvogn">El eftersyn personvogn</option>
                  <option value="El eftersyn lastvogn">El eftersyn lastvogn</option>
                  <option value="El eftersyn smeden">El eftersyn smeden</option>
                  <option value="El eftersyn plade">El eftersyn plade</option>
                  <option value="Kæde eftersyn">Kæde eftersyn</option>
                  <option value="Stige eftersyn">Stige eftersyn</option>
                  <option value="Kemiskab">Kemiskab</option>
                  <option value="Frigørelsesværktøj">Frigørelsesværktøj</option>
                  <option value="Nilfisk gulvvasker">Nilfisk gulvvasker</option>
                  <option value="Lift eftersyn">Lift eftersyn</option>
                  <option value="Port eftersyn">Port eftersyn</option>
                  <option value="Faldsikring">Faldsikring</option>
                  <option value="Førstehjælp/øjenskyld">Førstehjælp/øjenskyld</option>
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Godkendt status</label>
                  <select
                    value={formData.godkendt_status}
                    onChange={(e)=>setFormData({...formData, godkendt_status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  >
                    <option value="Godkendt">Godkendt</option>
                    <option value="Ikke godkendt">Ikke godkendt</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ansvarlig</label>
                  <input
                    type="text"
                    value={formData.ansvarlig}
                    onChange={(e)=>setFormData({...formData, ansvarlig: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Godkendt af</label>
                  <input
                    type="text"
                    value={formData.godkendt_af}
                    onChange={(e)=>setFormData({...formData, godkendt_af: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sidste eftersyn</label>
                  <input
                    type="date"
                    value={formData.sidste_eftersyn}
                    onChange={(e)=>setFormData({...formData, sidste_eftersyn: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Næste eftersyn</label>
                  <input
                    type="date"
                    value={formData.naeste_eftersyn}
                    onChange={(e)=>setFormData({...formData, naeste_eftersyn: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                >
                  <option value="Planlagt">Planlagt</option>
                  <option value="I gang">I gang</option>
                  <option value="Afsluttet">Afsluttet</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivelse
                </label>
                <textarea
                  value={formData.beskrivelse}
                  onChange={(e) => setFormData({...formData, beskrivelse: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Noter
                </label>
                <textarea
                  value={formData.noter}
                  onChange={(e) => setFormData({...formData, noter: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                  rows="2"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingEftersyn(null);
                    setFormData({
                      bil_id: '',
                      eftersyn_dato: '',
                      eftersyn_type: '',
                      beskrivelse: '',
                      status: 'Planlagt',
                      noter: ''
                    });
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Annuller
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-falck-blue text-white rounded-md hover:bg-blue-700"
                >
                  {editingEftersyn ? 'Opdater' : 'Tilføj'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Eftersyn;
